<template>
  <div id="app">
    <!-- Auth form -->
    <div v-if="!token" class="auth">
      <h2>Mini мессенджер</h2>
      <div>
        <input v-model="authLogin" placeholder="Имя пользователя" />
        <input type="password" v-model="authPassword" placeholder="Пароль" />
        <button @click="login">Войти</button>
        <button @click="register">Регистрация</button>
      </div>
      <div v-if="authError" class="error">{{ authError }}</div>
    </div>

    <!-- Main interface -->
    <div v-else class="app" :class="{ 'mobile': isMobile, 'sidebar-open': sidebarOpen }">
      <!-- Top header: brand + slogan + hamburger -->
      <header class="topbar">
        <div class="brand">
          <div class="brand-text">
            <div class="brand-name">Mini мессенджер</div>
            <div class="brand-slogan">Онлайн-чат — быстро и просто</div>
          </div>
        </div>
        <button class="hamburger" aria-label="Menu" aria-disabled="true">☰</button>
      </header>
      <!-- Sidebar (chats list) -->
      <div class="sidebar">
        <div class="user-info">
          Вы вошли как: <strong>{{ currentUser }}</strong>
          <button @click="logout">Выйти</button>
        </div>
        <div class="search">
          <input v-model="searchQuery" placeholder="🔍︎ Найти контакт..." @input="searchUsers" />
          <div class="search-results" v-if="searchResults.length">
            <div v-for="user in searchResults" :key="user.login" @click="startChat(user.login)">
              {{ user.login }}
            </div>
          </div>
        </div>
        <div class="chats">
          <div v-for="chat in chats" :key="chat.login" class="chat-item" @click="selectChat(chat.login)">
            <div class="chat-item-header">
              <strong>{{ chat.login }}</strong>
              <div class="last-seen" v-if="chat.lastSeen">
                был(а) {{ formatLastSeen(chat.lastSeen) }}
              </div>
            </div>
            <div class="last-message">{{ chat.lastMessage || 'Нет сообщений' }}</div>
            <div v-if="chat.unreadCount > 0" class="unread-badge">{{ chat.unreadCount }}</div>
          </div>
        </div>
      </div>

      <!-- Main chat area -->
      <div class="main">
        <div v-if="selectedChat" class="chat-header">
          <button v-if="isMobile && !sidebarOpen" class="back-button" @click="closeChat">↩</button>
          <span class="chat-name">
            <small>диалог с</small> {{ selectedChat }}
            <div class="chat-last-seen" v-if="currentChatLastSeen">
              {{ formatLastSeen(currentChatLastSeen) }}
            </div>
          </span>
        </div>
        <div v-if="selectedChat" class="messages" ref="messagesContainer">
          <div v-for="msg in messages" :key="msg._id" class="message" :class="{ own: msg.from === currentUser }">
            <div class="sender">{{ msg.from === currentUser ? 'Я' : msg.from }}</div>
            <div v-if="msg.type === 'image'" class="image-message">
              <img :src="msg.text" @click="openImage(msg.text)" class="message-image" />
            </div>
            <div v-else class="text">{{ msg.text }}</div>
            <div class="time">{{ new Date(msg.timestamp).toLocaleTimeString() }}</div>
          </div>
        </div>
        <div v-if="selectedChat" class="compose">
          <input 
            v-model="newMessage" 
            placeholder="Введите сообщение..." 
            @keyup.enter="sendMessage" 
          />
          <button class="attach-btn" @click="triggerImageUpload">[◉"]</button>
          <input 
            type="file" 
            ref="imageInput" 
            accept="image/*" 
            style="display: none" 
            @change="handleImageUpload" 
          />
          <button @click="sendMessage">➤</button>
        </div>
        <div v-else class="no-chat">Выберите диалог для начала общения</div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import io from 'socket.io-client';

// const API_URL = 'http://localhost:3000';
const API_URL = 'https://miniapi.wsweb.ru';


export default {
  data() {
    return {
      token: null,
      currentUser: null,
      authLogin: '',
      authPassword: '',
      authError: '',
      socket: null,
      chats: [],
      searchQuery: '',
      searchResults: [],
      selectedChat: null,
      messages: [],
      newMessage: '',
      isMobile: false,
      sidebarOpen: true,
      currentChatLastSeen: null
    };
  },
  async mounted() {
    // Добавляем глобальный стиль для body
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    
    const savedUser = this.getCookie('messenger_user');
    const savedToken = this.getCookie('messenger_token');
    
    if (savedUser && savedToken) {
      this.token = savedToken;
      this.currentUser = savedUser;
      this.initSocket();
      await this.loadChats();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
    // Восстанавливаем стиль body
    document.body.style.margin = '';
    document.body.style.padding = '';
  },
  methods: {
    setCookie(name, value, days = 30) {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
    },
    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    },
    deleteCookie(name) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
    },
    formatLastSeen(date) {
      if (!date) return '';
      const lastSeen = new Date(date);
      const now = new Date();
      const diffMs = now - lastSeen;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'только что';
      if (diffMins < 60) return `${diffMins} мин назад`;
      if (diffHours < 24) return `${diffHours} ч назад`;
      if (diffDays < 7) return `${diffDays} д назад`;
      return lastSeen.toLocaleDateString();
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
      if (this.isMobile) {
        if (this.selectedChat) {
          this.sidebarOpen = false;
        } else {
          this.sidebarOpen = true;
        }
      } else {
        this.sidebarOpen = true;
      }
    },
    triggerImageUpload() {
      this.$refs.imageInput.click();
    },
    async handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (!file.type.startsWith('image/')) {
        alert('Пожалуйста, выберите изображение');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        alert('Файл слишком большой (макс 10MB)');
        return;
      }
      
      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const base64 = e.target.result;
          
          this.socket.emit('private message', {
            to: this.selectedChat,
            text: base64,
            type: 'image'
          });
          
          this.messages.push({
            from: this.currentUser,
            text: base64,
            timestamp: new Date(),
            type: 'image'
          });
          
          this.scrollToBottom();
          this.updateChatList(this.selectedChat, '📷 Изображение', this.currentUser);
        };
        reader.readAsDataURL(file);
      } catch (err) {
        console.error('Error uploading image:', err);
        alert('Ошибка при загрузке изображения');
      }
      
      event.target.value = '';
    },
    openImage(url) {
      window.open(url, '_blank');
    },
    async register() {
      try {
        const res = await axios.post(`${API_URL}/register`, {
          login: this.authLogin,
          password: this.authPassword
        });
        this.handleAuthSuccess(res.data);
      } catch (err) {
        this.authError = err.response?.data?.error || 'Registration failed';
      }
    },
    async login() {
      try {
        const res = await axios.post(`${API_URL}/login`, {
          login: this.authLogin,
          password: this.authPassword
        });
        this.handleAuthSuccess(res.data);
      } catch (err) {
        this.authError = err.response?.data?.error || 'Login failed';
      }
    },
    handleAuthSuccess(data) {
      this.token = data.token;
      this.currentUser = data.login;
      
      this.setCookie('messenger_token', data.token);
      this.setCookie('messenger_user', data.login);
      
      this.authLogin = '';
      this.authPassword = '';
      this.authError = '';
      this.initSocket();
      this.loadChats();
      if (this.isMobile) this.sidebarOpen = true;
    },
    logout() {
      if (this.socket) {
        this.socket.emit('logout');
        this.socket.disconnect();
      }
      
      this.token = null;
      this.currentUser = null;
      
      this.deleteCookie('messenger_token');
      this.deleteCookie('messenger_user');
      
      this.chats = [];
      this.selectedChat = null;
      this.messages = [];
    },
    initSocket() {
      this.socket = io(API_URL, {
        auth: { token: this.token }
      });
      
      this.socket.on('new message', (msg) => {
        if (msg.from === this.currentUser) return;
        
        if (this.selectedChat && msg.from === this.selectedChat) {
          this.messages.push({
            from: msg.from,
            text: msg.text,
            timestamp: msg.timestamp,
            type: msg.type || 'text'
          });
          this.scrollToBottom();
        }
        
        this.updateChatList(msg.from, msg.type === 'image' ? '📷 Изображение' : msg.text, msg.from);
      });
      
      this.socket.on('user status', (data) => {
        const chat = this.chats.find(c => c.login === data.login);
        if (chat) {
          chat.lastSeen = data.lastSeen;
        }
        if (this.selectedChat === data.login) {
          this.currentChatLastSeen = data.lastSeen;
        }
      });
    },
    async loadChats() {
      try {
        const res = await axios.get(`${API_URL}/chats`, {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        
        const chatsWithLastMessage = await Promise.all(
          res.data.map(async (chat) => {
            try {
              const messagesRes = await axios.get(`${API_URL}/messages/${chat.login}`, {
                headers: { Authorization: `Bearer ${this.token}` }
              });
              
              const messages = messagesRes.data;
              const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
              
              return {
                login: chat.login,
                lastMessage: lastMessage ? 
                  (lastMessage.type === 'image' ? '📷 Изображение' :
                    (lastMessage.from === this.currentUser ? `Я: ${lastMessage.text}` : `${chat.login}: ${lastMessage.text}`)) 
                  : null,
                lastTimestamp: lastMessage ? new Date(lastMessage.timestamp) : new Date(0),
                unreadCount: 0,
                lastSeen: chat.lastSeen || null
              };
            } catch (err) {
              console.error(err);
              return {
                login: chat.login,
                lastMessage: null,
                lastTimestamp: new Date(0),
                unreadCount: 0,
                lastSeen: chat.lastSeen || null
              };
            }
          })
        );
        
        this.chats = chatsWithLastMessage.sort((a, b) => b.lastTimestamp - a.lastTimestamp);
      } catch (err) {
        console.error(err);
      }
    },
    async searchUsers() {
      if (!this.searchQuery.trim()) {
        this.searchResults = [];
        return;
      }
      try {
        const res = await axios.get(`${API_URL}/users`, {
          headers: { Authorization: `Bearer ${this.token}` },
          params: { search: this.searchQuery }
        });
        this.searchResults = res.data;
      } catch (err) {
        console.error(err);
      }
    },
    startChat(login) {
      this.searchQuery = '';
      this.searchResults = [];
      this.selectChat(login);
    },
    async selectChat(login) {
      this.selectedChat = login;
      await this.loadMessages(login);
      
      const chat = this.chats.find(c => c.login === login);
      if (chat) {
        chat.unreadCount = 0;
        this.currentChatLastSeen = chat.lastSeen;
      }
      
      if (this.isMobile) {
        this.sidebarOpen = false;
      }
    },
    closeChat() {
      this.selectedChat = null;
      this.messages = [];
      this.currentChatLastSeen = null;
      if (this.isMobile) {
        this.sidebarOpen = true;
      }
    },
    async loadMessages(otherUser) {
      try {
        const res = await axios.get(`${API_URL}/messages/${otherUser}`, {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        this.messages = res.data;
        this.scrollToBottom();
      } catch (err) {
        console.error(err);
      }
    },
    sendMessage() {
      if (!this.newMessage.trim() || !this.selectedChat) return;
      const text = this.newMessage;
      this.socket.emit('private message', {
        to: this.selectedChat,
        text,
        type: 'text'
      });
      this.messages.push({
        from: this.currentUser,
        text,
        timestamp: new Date(),
        type: 'text'
      });
      this.newMessage = '';
      this.scrollToBottom();
      this.updateChatList(this.selectedChat, text, this.currentUser);
    },
    updateChatList(login, text, fromUser) {
      const chat = this.chats.find(c => c.login === login);
      const isOwn = fromUser === this.currentUser;
      const prefix = isOwn ? 'Я: ' : `${login}: `;
      const lastMessage = text.startsWith('data:image') ? '📷 Изображение' : prefix + text;
      
      if (chat) {
        chat.lastMessage = lastMessage;
        chat.lastTimestamp = new Date();
        
        if (this.selectedChat !== login && !isOwn) {
          chat.unreadCount = (chat.unreadCount || 0) + 1;
        }
      } else {
        this.chats.unshift({ 
          login, 
          lastMessage, 
          lastTimestamp: new Date(),
          unreadCount: (this.selectedChat !== login && !isOwn) ? 1 : 0,
          lastSeen: null
        });
      }
      
      this.chats.sort((a, b) => b.lastTimestamp - a.lastTimestamp);
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.messages');
        if (container) container.scrollTop = container.scrollHeight;
      });
    }
  }
};
</script>

<style scoped>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: fixed;
  top: 0;
  left: 0;
}

#app {
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #1a472a 0%, #2d5a3b 100%);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}

/* Auth form - green theme */
.auth {
  background: #f5f7f0;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  width: 320px;
  border: 1px solid #6b8c5c;
}

.auth h2 {
  color: #2c5e2e;
  text-align: center;
  margin-bottom: 1.5rem;
  font-weight: 600;
}

.auth input {
  display: block;
  width: 100%;
  margin-bottom: 12px;
  padding: 10px 12px;
  border: 1px solid #b8c9a5;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.auth input:focus {
  outline: none;
  border-color: #3d6b3f;
  box-shadow: 0 0 0 3px rgba(61, 107, 63, 0.1);
}

.auth button {
  width: 48%;
  margin-right: 4%;
  padding: 10px;
  background: #2d5a2c;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.auth button:last-child {
  margin-right: 0;
  background: #5a7c4a;
}

.auth button:hover {
  background: #1e3a1e;
  transform: translateY(-1px);
}

.auth button:last-child:hover {
  background: #456a36;
}

.error {
  color: #c65d5d;
  margin-top: 12px;
  font-size: 0.85em;
  text-align: center;
}

/* Main app container */
.app {
  display: flex;
  width: 100%;
  height: 100%;
  background: #f0f4ea;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Make room for fixed topbar */
.app {
  padding-top: 64px;
}

.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #eef3e7;
  border-bottom: 2px solid #6b8c5c;
  z-index: 1000;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.brand-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e4a1e;
  line-height: 1;
}

.brand-slogan {
  font-size: 0.75rem;
  color: #6f8f62;
  line-height: 1;
}

.hamburger {
  background: none;
  border: none;
  font-size: 1.4rem;
  color: #4a6b3a;
  cursor: default; /* non-active */
  opacity: 0.7;
  padding: 8px;
}

/* Sidebar - dark green */
.sidebar {
  width: 320px;
  border-right: 1px solid #9bb87a;
  display: flex;
  flex-direction: column;
  background: #eef3e7;
  overflow: hidden;
}

/* User info section */
.user-info {
  padding: 16px 20px;
  border-bottom: 2px solid #6b8c5c;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #d9e6c3;
  color: #2c5e2e;
  flex-shrink: 0;
}

.user-info strong {
  color: #1e4a1e;
}

.user-info button {
  padding: 6px 14px;
  background: #4a6b3a;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s ease;
}

.user-info button:hover {
  background: #2c4a2a;
  transform: scale(0.98);
}

/* Search section */
.search {
  padding: 15px;
  border-bottom: 1px solid #b8c9a5;
  background: #eef3e7;
  flex-shrink: 0;
}

.search input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #b8c9a5;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.search input:focus {
  outline: none;
  border-color: #4a6b3a;
  box-shadow: 0 0 0 3px rgba(74, 107, 58, 0.1);
}

.search-results {
  background: white;
  border: 1px solid #b8c9a5;
  border-radius: 6px;
  margin-top: 8px;
  max-height: 150px;
  overflow-y: auto;
}

.search-results div {
  padding: 10px 12px;
  cursor: pointer;
  transition: background 0.2s ease;
  color: #2c5e2e;
}

.search-results div:hover {
  background: #d9e6c3;
  color: #1e4a1e;
}

/* Chats list */
.chats {
  flex: 1;
  overflow-y: auto;
  background: #f5f7f0;
}

.chat-item {
  padding: 14px 20px;
  border-bottom: 1px solid #d4e2c4;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.chat-item:hover {
  background: #e4efd9;
  transform: translateX(4px);
}

.chat-item strong {
  color: #2c5e2e;
  font-size: 15px;
}

.last-message {
  font-size: 0.8em;
  color: #6f8f62;
  margin-top: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.unread-badge {
  background: #4a6b3a;
  color: white;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: bold;
  display: inline-block;
  margin-top: 4px;
}

/* Main chat area */
.main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
  min-width: 0;
}

/* Chat header */
.chat-header {
  padding: 16px 20px;
  border-bottom: 2px solid #6b8c5c;
  background: #eef3e7;
  font-weight: 600;
  display: flex;
  align-items: center;
  color: #2c5e2e;
  flex-shrink: 0;
}

.chat-name {
  flex: 1;
  text-align: center;
  font-size: 18px;
  color: #1e4a1e;
}

.back-button {
  background: none;
  border: none;
  font-size: 1.5em;
  cursor: pointer;
  padding: 0 12px;
  margin-right: 10px;
  color: #4a6b3a;
  transition: color 0.3s ease;
}

.back-button:hover {
  color: #2c5e2e;
}

/* Messages area */
.messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  background: #fafcf7;
  min-height: 0;
}

.message {
  max-width: 70%;
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 18px;
  align-self: flex-start;
  background: #e4efd9;
  color: #2c3e2a;
  word-wrap: break-word;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.message.own {
  align-self: flex-end;
  background: #6b8c5c;
  color: white;
}

.sender {
  font-size: 0.7em;
  font-weight: bold;
  margin-bottom: 5px;
  opacity: 0.8;
}

.text {
  word-wrap: break-word;
  line-height: 1.4;
}

.time {
  font-size: 0.6em;
  text-align: right;
  margin-top: 5px;
  opacity: 0.7;
}

/* Compose area */
.compose {
  padding: 16px 20px;
  border-top: 1px solid #d4e2c4;
  display: flex;
  gap: 8px;
  background: #f5f7f0;
  flex-shrink: 0;
}

.compose input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #b8c9a5;
  border-radius: 24px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: white;
}

.compose input:focus {
  outline: none;
  border-color: #6b8c5c;
  box-shadow: 0 0 0 3px rgba(107, 140, 92, 0.1);
}

.compose button {
  padding: 0 24px;
  border-radius: 24px;
  background: #4a6b3a;
  color: white;
  border: none;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
}

.compose button:hover {
  background: #2c5e2e;
  transform: translateY(-1px);
}

.compose button:active {
  transform: translateY(0);
}

.compose button.attach-btn {
  background: none;
  color: rgb(222, 221, 221);
  cursor: pointer;
  font-size: 12px;
  margin-left: -70px;
}

.compose button.attach-btn:hover {
  color: #bcbbbb;
  transform: scale(1.05);
}

.no-chat {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #9bb87a;
  font-size: 16px;
  background: #fafcf7;
}

/* New styles for last seen */
.chat-item-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px;
}

.last-seen {
  font-size: 0.7em;
  color: #8faa7a;
  white-space: nowrap;
}

.chat-last-seen {
  font-size: 0.7em;
  color: #8faa7a;
  margin-top: 4px;
}

.image-message {
  max-width: 100%;
  margin: 4px 0;
}

.message-image {
  max-width: 200px;
  max-height: 200px;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.2s;
}

.message-image:hover {
  transform: scale(1.05);
}

/* Scrollbar styling */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #eef3e7;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #9bb87a;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #6b8c5c;
}

/* Mobile styles */
@media (max-width: 768px) {
  .app {
    flex-direction: row;
  }

  /* Reduce topbar height on mobile */
  .app {
    padding-top: 52px;
  }

  .topbar {
    height: 52px;
    padding: 6px 12px;
  }
  .brand-name { font-size: 0.95rem; }
  .brand-slogan { font-size: 0.65rem; }
  
  .sidebar {
    width: 100%;
  }
  
  .app.mobile .sidebar {
    display: block;
    width: 100%;
  }
  
  .app.mobile .main {
    display: block;
    width: 100%;
  }
  
  .app.mobile.sidebar-open .sidebar {
    display: block;
  }
  
  .app.mobile.sidebar-open .main {
    display: none;
  }
  
  .app.mobile:not(.sidebar-open) .sidebar {
    display: none;
  }
  
  .app.mobile:not(.sidebar-open) .main {
    display: flex;
  }
  
  .chat-header {
    padding: 12px 16px;
  }
  
  .back-button {
    font-size: 1.2em;
    padding: 0 8px;
  }
  
  .chat-name {
    font-size: 16px;
  }
  
  .message {
    max-width: 85%;
  }
  
  .message-image {
    max-width: 150px;
    max-height: 150px;
  }
  
  .last-seen {
    font-size: 0.6em;
  }
}
</style>